import { Input } from "antd";
import queryClient from "api/queryClient";
import { useUpdateFormStructureMutation } from "api/TenantSlug/FormStructure/hooks";
import {
    FORM_STRUCTURE_SYSTEM_NAME_REGISTRATION_ADVERTISER,
    FORM_STRUCTURE_SYSTEM_NAME_REGISTRATION_AFFILIATE_PARTNER,
} from "components/Tables/FormStructureTable/FormStructureTable";
import { FormCol } from "components/UI/Form/FormCol";
import { FormGrid } from "components/UI/Form/FormGrid";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { LocationSectionHeader } from "components/UI/LocationSectionHeader";
import { CategorySelectSearchable } from "components/UI/Selects/CategorySelectSearchable/CategorySelectSearchable.tsx";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { LabelSelectSearchable } from "components/UI/Selects/LabelSelectSearchable/LabelSelectSearchable.tsx";
import { OfferSelectSearchable } from "components/UI/Selects/OfferSelectSearchable/OfferSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useEffect } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { FormId } from "types/models.ts";

import { formSchema, FormStructureFormData } from "./formSchema.ts";

import FormStructureData = App.Data.FormStructureData;

interface FormStructureFormProps {
    formStructure: FormStructureData;
}

interface FormStructureGeneralFormContentProps {
    formStructure: FormStructureData;
}

const FormStructureGeneralFormContent: FC<FormStructureGeneralFormContentProps> = ({ formStructure }) => {
    const { t: tFields } = usePaldockTranslation("forms.general-fields");

    const generalFormUtils = useGeneralForm<FormStructureFormData>();
    const { control, fieldNames, reset } = generalFormUtils;

    const isDisabled =
        formStructure?.system_name === FORM_STRUCTURE_SYSTEM_NAME_REGISTRATION_AFFILIATE_PARTNER ||
        formStructure?.system_name === FORM_STRUCTURE_SYSTEM_NAME_REGISTRATION_ADVERTISER;

    // Reset form when formStructure changes (after refetch)
    useEffect(() => {
        if (formStructure) {
            // Type assertion needed until BE types are updated with country field
            const formStructureWithCountry = formStructure as FormStructureData & { country?: App.Data.CountryData };
            reset({
                name: formStructure.name,
                system_name: formStructure.system_name,
                offers: formStructure.offers?.map((o) => o.id) || [],
                country_id: formStructureWithCountry.country?.id ?? null,
                category: formStructure.category?.id ?? null,
                label_ids: formStructure.labels?.map((l) => l.id) || [],
            });
        }
    }, [formStructure, reset]);

    if (!formStructure) {
        return null;
    }

    return (
        <>
            <LocationSectionHeader />
            <FormCol>
                <FormGrid cols={3}>
                    <FormItem
                        control={control}
                        name={fieldNames.name?.name || "name"}
                        label={tFields("name.label")}
                        required
                    >
                        <Input placeholder={tFields("name.placeholder")} disabled={isDisabled} />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.system_name?.name || "system_name"}
                        label={tFields("system_name.label")}
                        required
                    >
                        <Input placeholder={tFields("system_name.placeholder")} disabled={isDisabled} />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.country_id?.name || "country_id"}
                        label={tFields("country.label")}
                    >
                        <CountrySelectSearchable allowClear />
                    </FormItem>
                </FormGrid>
                <FormGrid cols={3}>
                    <FormItem
                        control={control}
                        name={fieldNames.offers?.name || "offers"}
                        label={tFields("offers.label")}
                    >
                        <OfferSelectSearchable mode="multiple" />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.category?.name || "category"}
                        label={tFields("category.label")}
                    >
                        <CategorySelectSearchable allowClear />
                    </FormItem>
                    <FormItem control={control} name="label_ids" label={tFields("label.label")}>
                        <LabelSelectSearchable mode="multiple" allowClear />
                    </FormItem>
                </FormGrid>
            </FormCol>
        </>
    );
};

export const FormStructureGeneralForm: FC<FormStructureFormProps> = ({ formStructure }) => {
    const { t } = usePaldockTranslation("forms.form-structure-general");
    const updateFormStructureMutation = useUpdateFormStructureMutation(formStructure?.id || "");

    const handleSubmit = (values: FormStructureFormData) => {
        // Type assertion needed until BE types are updated with country_id field
        updateFormStructureMutation.mutate(
            {
                id: formStructure.id,
                system_name: values.system_name,
                name: values.name,
                offer_ids: values.offers || [],
                type: formStructure.type,
                country_id: values.country_id ?? null,
                category_id: values.category ?? null,
                label_ids: values.label_ids || [],
            } as Parameters<typeof updateFormStructureMutation.mutate>[0],
            {
                onSuccess: (response) => {
                    if (response && response.formStructure) {
                        queryClient.invalidateQueries({
                            queryKey: ["getFormStructures"],
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["getFormStructure", formStructure.id],
                        });
                        toast.success(t("messages.update_success"));
                    }
                },
                onError: () => {
                    toast.error(t("messages.update_error"));
                },
            }
        );
    };

    // Type assertion needed until BE types are updated with country field
    const formStructureWithCountry = formStructure as FormStructureData & { country?: App.Data.CountryData };
    const defaultValues = {
        name: formStructure?.name || "",
        system_name: formStructure?.system_name || "",
        offers: formStructure?.offers?.map((o) => o.id) || [],
        country_id: formStructureWithCountry?.country?.id ?? null,
        category: formStructure?.category?.id ?? null,
        label_ids: formStructure?.labels?.map((l) => l.id) || [],
    };

    if (!formStructure) {
        return null;
    }

    return (
        <GeneralFormProvider<FormStructureFormData>
            key={formStructure.id}
            schema={formSchema}
            id={FormId.FormStructureForm}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            resetOnSubmit={false}
        >
            <FormStructureGeneralFormContent formStructure={formStructure} />
        </GeneralFormProvider>
    );
};
